import React from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Bell,
  Shield,
  Play
} from 'lucide-react';

export interface AutomationTemplateData {
  id: string;
  name: string;
  description: string;
  iconName: string;
  category: 'performance' | 'budget' | 'notification' | 'optimization';
  platform: string[];
  trigger_type: string;
  trigger_value: number;
  action_type: string;
  action_value: number;
  frequency: string;
  popularity: number;
}

export const AUTOMATION_TEMPLATES: AutomationTemplateData[] = [
  {
    id: 'pause-high-cpc',
    name: 'Pausar CPC Alto',
    description: 'Pausa automaticamente anúncios quando o CPC ultrapassa um limite definido',
    iconName: 'DollarSign',
    category: 'budget',
    platform: ['google', 'meta', 'linkedin'],
    trigger_type: 'cpc_above',
    trigger_value: 5,
    action_type: 'pause_ad',
    action_value: 0,
    frequency: 'hourly',
    popularity: 95
  },
  {
    id: 'boost-roas',
    name: 'Aumentar Lance ROAS Alto',
    description: 'Aumenta lance automaticamente quando ROAS está acima do objetivo',
    iconName: 'TrendingUp',
    category: 'optimization',
    platform: ['google', 'meta', 'linkedin'],
    trigger_type: 'roas_above',
    trigger_value: 4,
    action_type: 'adjust_bid',
    action_value: 15,
    frequency: 'daily',
    popularity: 88
  },
  {
    id: 'alert-low-ctr',
    name: 'Alerta CTR Baixo',
    description: 'Envia notificação quando CTR cai abaixo do esperado',
    iconName: 'Bell',
    category: 'notification',
    platform: ['google', 'meta', 'linkedin'],
    trigger_type: 'ctr_below',
    trigger_value: 1.5,
    action_type: 'send_notification',
    action_value: 0,
    frequency: 'realtime',
    popularity: 82
  },
  {
    id: 'budget-protection',
    name: 'Proteção de Orçamento',
    description: 'Pausa campanhas antes de ultrapassar o orçamento diário',
    iconName: 'Shield',
    category: 'budget',
    platform: ['google', 'meta', 'linkedin'],
    trigger_type: 'spend_above',
    trigger_value: 90,
    action_type: 'pause_campaign',
    action_value: 0,
    frequency: 'hourly',
    popularity: 91
  },
  {
    id: 'reduce-low-roas',
    name: 'Reduzir Lance ROAS Baixo',
    description: 'Diminui lance automaticamente quando ROAS está abaixo do objetivo',
    iconName: 'TrendingDown',
    category: 'optimization',
    platform: ['google', 'meta', 'linkedin'],
    trigger_type: 'roas_below',
    trigger_value: 2,
    action_type: 'adjust_bid',
    action_value: -20,
    frequency: 'daily',
    popularity: 79
  },
  {
    id: 'reactivate-performer',
    name: 'Reativar Alto Performer',
    description: 'Reativa anúncios pausados que tinham bom desempenho histórico',
    iconName: 'Play',
    category: 'optimization',
    platform: ['google', 'meta', 'linkedin'],
    trigger_type: 'ctr_above',
    trigger_value: 3,
    action_type: 'enable_ad',
    action_value: 0,
    frequency: 'daily',
    popularity: 65
  }
];

export const getTriggerLabel = (type: string): string => {
  const triggers: Record<string, string> = {
    ctr_below: 'CTR abaixo de',
    ctr_above: 'CTR acima de',
    cpc_above: 'CPC acima de',
    cpc_below: 'CPC abaixo de',
    spend_above: 'Gasto acima de',
    conversions_below: 'Conversões abaixo de',
    roas_below: 'ROAS abaixo de',
    roas_above: 'ROAS acima de',
    cpa_above: 'CPA acima de',
    cpa_below: 'CPA abaixo de'
  };
  return triggers[type] || type;
};

export const getActionLabel = (type: string): string => {
  const actions: Record<string, string> = {
    adjust_bid: 'Ajustar lance',
    pause_ad: 'Pausar anúncio',
    enable_ad: 'Ativar anúncio',
    pause_campaign: 'Pausar campanha',
    enable_campaign: 'Ativar campanha',
    send_notification: 'Enviar notificação',
    adjust_budget: 'Ajustar orçamento'
  };
  return actions[type] || type;
};

export const getFrequencyLabel = (freq: string): string => {
  const frequencies: Record<string, string> = {
    realtime: 'Tempo real',
    hourly: 'A cada hora',
    daily: 'Diariamente',
    weekly: 'Semanalmente'
  };
  return frequencies[freq] || freq;
};

export const formatTimeAgo = (date: string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `há ${diffMins} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  return `há ${diffDays}d`;
};

export const getTemplateIcon = (iconName: string) => {
  const icons: Record<string, any> = {
    DollarSign,
    TrendingUp,
    TrendingDown,
    Bell,
    Shield,
    Play
  };
  return icons[iconName] || DollarSign;
};

export const getCategoryColor = (category: string) => {
  const colors: Record<string, { bg: string; text: string }> = {
    budget: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-600 dark:text-green-400' },
    optimization: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-600 dark:text-blue-400' },
    notification: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-600 dark:text-yellow-400' },
    performance: { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-600 dark:text-purple-400' }
  };
  return colors[category] || colors.performance;
};
